import { ArrowRight, BriefcaseBusiness, CalendarDays, CheckCircle2, CircleDollarSign, FileText, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { createAdminProposal, getAdminLeads, getAdminProposals } from "../../lib/api";
import type { AdminProposalRecord } from "../../lib/types";
import { FormPortalHeader } from "./FormPortalHeader";

type LeadRecord = Awaited<ReturnType<typeof getAdminLeads>>["leads"][number];

type ProposalLineForm = {
  id: string;
  name: string;
  description: string;
  quantity: string;
  unitCents: string;
  lineType: string;
};

type ProposalForm = {
  leadId: string;
  clientId: string;
  packageName: string;
  recommendedServices: string;
  selectedServices: string;
  recommendedAddons: string;
  notes: string;
  expirationDate: string;
  discountDollars: string;
  depositDollars: string;
  statusMode: "draft" | "ready";
};

const blankLine = (): ProposalLineForm => ({
  id: crypto.randomUUID(),
  name: "Kreatethedate Proposal Package",
  description: "Primary event coverage and creative direction package.",
  quantity: "1",
  unitCents: "0",
  lineType: "service",
});

export function ProposalWorkspacePage() {
  const [leads, setLeads] = useState<LeadRecord[]>([]);
  const [proposals, setProposals] = useState<AdminProposalRecord[]>([]);
  const [form, setForm] = useState<ProposalForm>({
    leadId: "",
    clientId: "",
    packageName: "Custom Kreatethedate Proposal",
    recommendedServices: "",
    selectedServices: "",
    recommendedAddons: "",
    notes: "",
    expirationDate: "",
    discountDollars: "0",
    depositDollars: "",
    statusMode: "draft",
  });
  const [lines, setLines] = useState<ProposalLineForm[]>([blankLine()]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Create Proposal | Kreatethedate Command Center";
    void loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    setError("");
    try {
      const [leadData, proposalData] = await Promise.all([getAdminLeads(), getAdminProposals()]);
      setLeads(leadData.leads || []);
      setProposals(proposalData.proposals || []);
      if (!form.leadId && leadData.leads?.length) {
        setForm((current) => ({ ...current, leadId: leadData.leads[0].id }));
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load proposal workspace.");
    } finally {
      setLoading(false);
    }
  }

  const selectedLead = leads.find((lead) => lead.id === form.leadId);

  const totals = useMemo(() => {
    const subtotal = lines.reduce((sum, line) => {
      const quantity = Math.max(1, Number(line.quantity) || 1);
      const unitCents = Math.max(0, Math.round((Number(line.unitCents) || 0) * 100));
      return sum + quantity * unitCents;
    }, 0);
    const discountCents = Math.max(0, Math.round((Number(form.discountDollars) || 0) * 100));
    const depositCents = Math.max(0, Math.round((Number(form.depositDollars) || 0) * 100));
    const totalCents = Math.max(0, subtotal - discountCents);
    const balanceCents = Math.max(0, totalCents - depositCents);
    return { subtotal, discountCents, depositCents, totalCents, balanceCents };
  }, [form.discountDollars, form.depositDollars, lines]);

  async function submitProposal(statusMode: "draft" | "ready") {
    if (!form.leadId) {
      setError("Please choose an existing lead before creating the proposal.");
      return;
    }
    if (!form.packageName.trim()) {
      setError("Please enter a proposal package name.");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");
    try {
      await createAdminProposal({
        action: statusMode === "ready" ? "ready_to_send" : "save_draft",
        leadId: form.leadId,
        clientId: form.clientId || undefined,
        packageName: form.packageName,
        recommendedServices: form.recommendedServices,
        selectedServices: form.selectedServices,
        recommendedAddons: form.recommendedAddons,
        notes: form.notes,
        expirationDate: form.expirationDate || undefined,
        subtotalCents: totals.subtotal,
        discountCents: totals.discountCents,
        depositCents: totals.depositCents,
        totalCents: totals.totalCents,
        lineItems: lines.map((line) => ({
          name: line.name,
          description: line.description,
          quantity: Number(line.quantity) || 1,
          unitCents: Math.round((Number(line.unitCents) || 0) * 100),
          lineType: line.lineType,
        })),
      });

      setMessage(statusMode === "ready" ? "Proposal saved and marked ready to send." : "Proposal draft saved.");
      setForm((current) => ({
        ...current,
        packageName: "Custom Kreatethedate Proposal",
        recommendedServices: "",
        selectedServices: "",
        recommendedAddons: "",
        notes: "",
        expirationDate: "",
        discountDollars: "0",
        depositDollars: "",
        statusMode,
      }));
      setLines([blankLine()]);
      await loadData();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to save proposal.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="command-center-shell min-h-screen text-[#fff9ef]">
      <FormPortalHeader />
      <section className="relative isolate overflow-hidden border-b border-[#FF6A2B]/18 bg-[#050505] px-4 py-12 text-[#fff9ef] sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,106,43,.18),transparent_34%),linear-gradient(120deg,rgba(5,5,5,.96),rgba(22,18,10,.9))]" />
        <div className="relative mx-auto grid max-w-7xl gap-5">
          <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[#FF6A2B]/35 px-3 py-2 text-xs font-black uppercase tracking-[0.2em] text-[#FF6A2B]">
            <FileText className="size-4" aria-hidden="true" />
            Proposal Creation Workspace
          </div>
          <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">Create Proposal</h1>
          <p className="max-w-3xl text-base font-semibold leading-8 text-[#fff9ef]/76">
            Build a draft proposal for an existing lead, add packages and add-ons, calculate pricing, and mark the proposal
            ready to send without falling back to a placeholder page.
          </p>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.1fr_.9fr]">
          <div className="grid gap-6">
            <article id="proposal-editor" className="command-center-card rounded-lg p-5 sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">Create Proposal</p>
                  <h2 className="mt-2 font-display text-3xl font-bold text-[#fff9ef]">Proposal Builder</h2>
                </div>
                <Link to="/admin/proposal-dashboard" className="admin-secondary-button" title="Open the executive proposal dashboard">
                  <ArrowRight className="size-4" aria-hidden="true" />
                  Open Proposal Dashboard
                </Link>
              </div>
              <div className="mt-6 grid gap-4 md:grid-cols-2">
                <SelectField
                  label="Existing Lead"
                  value={form.leadId}
                  onChange={(value) => setForm((current) => ({ ...current, leadId: value }))}
                >
                  <option value="">Select a lead</option>
                  {leads.map((lead) => (
                    <option key={lead.id} value={lead.id}>
                      {lead.fullName} · {lead.email}
                    </option>
                  ))}
                </SelectField>
                <TextField
                  label="Client ID / Record Link"
                  value={form.clientId}
                  onChange={(value) => setForm((current) => ({ ...current, clientId: value }))}
                  placeholder="Optional client record ID"
                />
                <TextField
                  label="Proposal Package Name"
                  value={form.packageName}
                  onChange={(value) => setForm((current) => ({ ...current, packageName: value }))}
                  placeholder="Custom Kreatethedate Proposal"
                />
                <TextField
                  label="Expiration Date"
                  type="date"
                  value={form.expirationDate}
                  onChange={(value) => setForm((current) => ({ ...current, expirationDate: value }))}
                />
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-3">
                <NumberField
                  label="Discount"
                  value={form.discountDollars}
                  onChange={(value) => setForm((current) => ({ ...current, discountDollars: value }))}
                  prefix="$"
                />
                <NumberField
                  label="Deposit"
                  value={form.depositDollars}
                  onChange={(value) => setForm((current) => ({ ...current, depositDollars: value }))}
                  prefix="$"
                />
                <TextAreaField
                  label="Selected Services"
                  value={form.selectedServices}
                  onChange={(value) => setForm((current) => ({ ...current, selectedServices: value }))}
                  placeholder="Wedding planning, content capture, event design..."
                />
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2">
                <TextAreaField
                  label="Recommended Services"
                  value={form.recommendedServices}
                  onChange={(value) => setForm((current) => ({ ...current, recommendedServices: value }))}
                  placeholder="Services recommended for this lead"
                />
                <TextAreaField
                  label="Recommended Add-ons"
                  value={form.recommendedAddons}
                  onChange={(value) => setForm((current) => ({ ...current, recommendedAddons: value }))}
                  placeholder="Albums, extra coverage, design upgrades..."
                />
              </div>

              <div className="mt-6">
                <TextAreaField
                  label="Proposal Notes"
                  value={form.notes}
                  onChange={(value) => setForm((current) => ({ ...current, notes: value }))}
                  placeholder="Internal notes, assumptions, special instructions..."
                />
              </div>

              <div className="mt-8 border-t border-[#FF6A2B]/14 pt-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Line Items</p>
                    <p className="mt-1 text-sm font-semibold text-[#fff9ef]/72">Add the services and add-ons that make up the proposal total.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setLines((current) => [...current, blankLine()])}
                    className="admin-secondary-button"
                    title="Add another proposal line item"
                  >
                    <Plus className="size-4" aria-hidden="true" />
                    Add Line Item
                  </button>
                </div>

                <div className="mt-4 grid gap-3">
                  {lines.map((line, index) => (
                    <div key={line.id} className="rounded-lg border border-[#FF6A2B]/18 bg-white/5 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-sm font-black uppercase tracking-[0.12em] text-[#FF6A2B]">
                          Line Item {index + 1}
                        </p>
                        <button
                          type="button"
                          onClick={() =>
                            setLines((current) => {
                              const next = current.filter((entry) => entry.id !== line.id);
                              return next.length ? next : [blankLine()];
                            })
                          }
                          className="inline-flex items-center gap-2 rounded-full border border-red-400/25 px-3 py-2 text-xs font-black uppercase tracking-[0.12em] text-red-100 transition hover:bg-red-500/10"
                          title="Remove this line item"
                        >
                          <Trash2 className="size-3.5" aria-hidden="true" />
                          Remove
                        </button>
                      </div>
                      <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <TextField
                          label="Item Name"
                          value={line.name}
                          onChange={(value) =>
                            setLines((current) => current.map((entry) => (entry.id === line.id ? { ...entry, name: value } : entry)))
                          }
                          placeholder="Wedding planning"
                        />
                        <TextField
                          label="Line Type"
                          value={line.lineType}
                          onChange={(value) =>
                            setLines((current) => current.map((entry) => (entry.id === line.id ? { ...entry, lineType: value } : entry)))
                          }
                          placeholder="service or addon"
                        />
                        <NumberField
                          label="Quantity"
                          value={line.quantity}
                          onChange={(value) =>
                            setLines((current) => current.map((entry) => (entry.id === line.id ? { ...entry, quantity: value } : entry)))
                          }
                        />
                        <NumberField
                          label="Unit Price"
                          value={line.unitCents}
                          onChange={(value) =>
                            setLines((current) => current.map((entry) => (entry.id === line.id ? { ...entry, unitCents: value } : entry)))
                          }
                          prefix="$"
                        />
                      </div>
                      <div className="mt-4">
                        <TextAreaField
                          label="Description"
                          value={line.description}
                          onChange={(value) =>
                            setLines((current) => current.map((entry) => (entry.id === line.id ? { ...entry, description: value } : entry)))
                          }
                          placeholder="Scope, deliverables, and notes for this line item"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-4">
                <StatCard label="Subtotal" value={money(totals.subtotal)} note="Calculated from line items." icon={BriefcaseBusiness} />
                <StatCard label="Discount" value={money(totals.discountCents)} note="Manual discount adjustment." icon={CircleDollarSign} />
                <StatCard label="Total" value={money(totals.totalCents)} note="Subtotal minus discount." icon={CheckCircle2} />
                <StatCard label="Balance Due" value={money(totals.balanceCents)} note="Remaining balance after deposit." icon={CalendarDays} />
              </div>

              {selectedLead ? (
                <div className="mt-6 rounded-lg border border-[#FF6A2B]/20 bg-black/20 p-4">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">Selected Lead</p>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-bold text-[#fff9ef]">{selectedLead.fullName}</p>
                      <p className="text-sm text-[#fff9ef]/72">{selectedLead.email} · {selectedLead.source}</p>
                    </div>
                    <Link to={`/admin/leads/${selectedLead.id}`} className="text-sm font-bold text-[#FF6A2B] hover:underline">
                      Open Lead Record
                    </Link>
                  </div>
                </div>
              ) : null}

              {message ? <p className="mt-5 rounded-lg border border-[#FF6A2B]/20 bg-white/5 px-4 py-3 text-sm font-bold text-[#fff9ef]">{message}</p> : null}
              {error ? <p className="mt-5 rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm font-bold text-red-100">{error}</p> : null}

              <div className="mt-6 flex flex-wrap gap-3">
                <button type="button" onClick={() => void submitProposal("draft")} disabled={saving} className="admin-primary-button" title="Save this proposal as a draft">
                  {saving ? "Saving..." : "Save Draft"}
                </button>
                <button type="button" onClick={() => void submitProposal("ready")} disabled={saving} className="admin-secondary-button" title="Mark the proposal ready for sending">
                  {saving ? "Saving..." : "Mark Ready to Send"}
                </button>
              </div>
            </article>
          </div>

          <aside className="grid gap-6">
            <article id="proposal-records" className="command-center-card rounded-lg p-5 sm:p-6">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">Proposal Records</p>
              <h2 className="mt-2 font-display text-3xl font-bold text-[#fff9ef]">Real Proposal Data</h2>
              <p className="mt-3 text-sm font-semibold leading-7 text-[#fff9ef]/72">
                These records are pulled from the live Forms Portal D1 proposal tables instead of a placeholder route.
              </p>
              <div className="mt-5 grid gap-3">
                {proposals.length ? (
                  proposals.slice(0, 10).map((proposal) => (
                    <div key={proposal.id} className="rounded-lg border border-[#FF6A2B]/18 bg-white/5 p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-black uppercase tracking-[0.12em] text-[#FF6A2B]">{proposal.proposalNumber}</p>
                          <p className="mt-1 font-bold text-[#fff9ef]">{proposal.leadName}</p>
                          <p className="text-sm text-[#fff9ef]/72">{proposal.leadEmail} · {proposal.leadSource}</p>
                        </div>
                        <span className="rounded-full border border-[#FF6A2B]/25 px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B]">
                          {proposal.status}
                        </span>
                      </div>
                      <div className="mt-3 grid gap-1 text-sm text-[#fff9ef]/72">
                        <p>{proposal.packageName || "Custom package"}</p>
                        <p>{money(proposal.totalCents)} total · {proposal.lineItemCount} line item{proposal.lineItemCount === 1 ? "" : "s"}</p>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <Link
                          to={`/admin/leads/${proposal.leadId}`}
                          className="inline-flex min-h-11 items-center justify-center rounded-lg border border-[#FF6A2B]/24 bg-white/5 px-4 text-sm font-extrabold text-[#fff9ef] transition hover:border-[#FF6A2B] hover:bg-white/10"
                          title="Open the linked lead record"
                        >
                          Open Lead
                        </Link>
                        <a
                          href={proposal.reviewUrl || `/proposal/${proposal.token}`}
                          className="inline-flex min-h-11 items-center justify-center rounded-lg border border-[#FF6A2B]/24 bg-white/5 px-4 text-sm font-extrabold text-[#fff9ef] transition hover:border-[#FF6A2B] hover:bg-white/10"
                          title="Open the proposal review URL"
                        >
                          Open Review Link
                        </a>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-lg border border-dashed border-white/15 bg-black/15 p-4 text-sm font-semibold text-[#fff9ef]/72">
                  No proposal records yet. Save the first draft above and it will appear here.
                </div>
              )}
              </div>
            </article>

            <article className="command-center-card rounded-lg p-5 sm:p-6">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">Workflow Note</p>
              <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]/72">
                Save Draft keeps the proposal internal. Mark Ready to Send advances the lead to Proposal Approved to Send
                so an administrator can review and publish it at the right time.
              </p>
              <div className="mt-4 rounded-lg border border-[#FF6A2B]/18 bg-white/5 p-4">
                <p className="text-sm font-black uppercase tracking-[0.12em] text-[#FF6A2B]">Quick Links</p>
                <div className="mt-3 grid gap-2">
                  <Link to="/admin/proposal-dashboard" className="focus-ring inline-flex items-center gap-2 text-sm font-bold text-[#fff9ef] hover:text-[#FF6A2B]">
                    <ArrowRight className="size-4" aria-hidden="true" />
                    Open Proposal Dashboard
                  </Link>
                  <Link to="/admin/leads" className="focus-ring inline-flex items-center gap-2 text-sm font-bold text-[#fff9ef] hover:text-[#FF6A2B]">
                    <ArrowRight className="size-4" aria-hidden="true" />
                    Open Lead Management
                  </Link>
                </div>
              </div>
            </article>
          </aside>
        </div>

        {loading ? <p className="mx-auto mt-8 max-w-7xl rounded-lg border border-[#FF6A2B]/20 bg-white/5 px-5 py-4 text-sm font-bold text-[#fff9ef]">Loading proposal workspace data...</p> : null}
      </section>
    </main>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className="grid gap-2 text-sm font-bold text-[#fff9ef]">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-lg border border-[#FF6A2B]/18 bg-black/30 px-4 py-3 text-[#fff9ef] outline-none transition placeholder:text-[#fff9ef]/35 focus:border-[#FF6A2B] focus:bg-black/45"
      />
    </label>
  );
}

function NumberField({
  label,
  value,
  onChange,
  prefix,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  prefix?: string;
}) {
  return (
    <label className="grid gap-2 text-sm font-bold text-[#fff9ef]">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{label}</span>
      <div className="flex items-center rounded-lg border border-[#FF6A2B]/18 bg-black/30 transition focus-within:border-[#FF6A2B] focus-within:bg-black/45">
        {prefix ? <span className="px-4 text-sm font-black text-[#FF6A2B]">{prefix}</span> : null}
        <input
          type="number"
          min="0"
          step="0.01"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full bg-transparent px-4 py-3 text-[#fff9ef] outline-none placeholder:text-[#fff9ef]/35"
        />
      </div>
    </label>
  );
}

function TextAreaField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="grid gap-2 text-sm font-bold text-[#fff9ef]">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{label}</span>
      <textarea
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        rows={4}
        className="w-full rounded-lg border border-[#FF6A2B]/18 bg-black/30 px-4 py-3 text-[#fff9ef] outline-none transition placeholder:text-[#fff9ef]/35 focus:border-[#FF6A2B] focus:bg-black/45"
      />
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
}) {
  return (
    <label className="grid gap-2 text-sm font-bold text-[#fff9ef]">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-lg border border-[#FF6A2B]/18 bg-black/30 px-4 py-3 text-[#fff9ef] outline-none transition focus:border-[#FF6A2B] focus:bg-black/45"
      >
        {children}
      </select>
    </label>
  );
}

function StatCard({
  label,
  value,
  note,
  icon: Icon,
}: {
  label: string;
  value: string;
  note: string;
  icon: ComponentType<{ className?: string }>;
}) {
  return (
    <div className="rounded-lg border border-[#FF6A2B]/18 bg-white/5 p-4" title={note}>
      <Icon className="size-5 text-[#FF6A2B]" aria-hidden="true" />
      <p className="mt-3 text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{label}</p>
      <p className="mt-2 font-display text-2xl font-bold text-[#fff9ef]">{value}</p>
      <p className="mt-2 text-sm font-semibold leading-6 text-[#fff9ef]/70">{note}</p>
    </div>
  );
}

function money(cents: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((Number(cents) || 0) / 100);
}
